import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import usePageMeta from "@/hooks/usePageMeta";
import CategoryBadge from "@/components/feature/CategoryBadge";
import NewsCard from "@/components/feature/NewsCard";
import { getNewsById, getRelated, formatDate, formatViews } from "@/lib/content";
import ShareModal from "./components/ShareModal";

export default function DetailPage() {
  const { id = "" } = useParams();
  const item = getNewsById(id);
  const [shareOpen, setShareOpen] = useState(false);

  const summaryText = item ? item.content[0].slice(0, 80) : "内容不存在";

  usePageMeta({
    title: item ? `${item.title} - 小蜜桃详细报道` : "内容不存在 - 小蜜桃",
    description: item ? summaryText : "你访问的内容不存在或已被移除。",
    keywords: item
      ? `${item.title},小蜜桃,${item.categoryName},${item.tags.join(",")}`
      : "小蜜桃",
  });

  if (!item) {
    return (
      <SiteLayout>
        <div className="w-full px-4 md:px-6 py-24 flex flex-col items-center text-center">
          <span className="w-14 h-14 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center">
            <i className="ri-file-unknow-line text-3xl"></i>
          </span>
          <h1 className="mt-5 font-heading font-bold text-2xl text-foreground-950">
            这篇内容不存在
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            它可能已被移除或链接有误，去看看其他精彩内容吧。
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm px-5 py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const related = getRelated(item, 3);

  return (
    <SiteLayout>
      <article className="w-full">
        <div className="w-full px-4 md:px-6 pt-6 md:pt-8">
          <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 flex-wrap">
            <Link to="/" className="hover:text-primary-600 transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link
              to={`/category/${item.categoryKey}`}
              className="hover:text-primary-600 transition-colors"
            >
              {item.categoryName}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-900 line-clamp-1">{item.subType}</span>
          </nav>
        </div>

        <div className="w-full px-4 md:px-6 mt-5">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 min-w-0">
              <CategoryBadge categoryKey={item.categoryKey} name={item.categoryName} />
              <h1 className="mt-4 font-heading font-bold text-2xl md:text-4xl text-foreground-950 leading-tight">
                {item.title}
              </h1>
              <p className="mt-3 text-base md:text-lg text-foreground-600">{item.subtitle}</p>

              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {item.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {formatDate(item.publishDate)}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line"></i>
                  {formatViews(item.views)} 阅读
                </span>
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="ml-auto inline-flex items-center gap-2 text-sm text-foreground-700 hover:text-primary-600 border border-background-200 rounded-full px-4 py-1.5 transition-colors cursor-pointer"
                >
                  <i className="ri-share-forward-line"></i>
                  分享
                </button>
              </div>

              <div className="mt-6 w-full h-64 md:h-[420px] rounded-lg overflow-hidden bg-background-200">
                <img
                  src={item.cover}
                  alt={`${item.title} - 小蜜桃配图`}
                  title={`${item.title} - 小蜜桃`}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="mt-8 max-w-3xl">
                {item.content.map((para, idx) => (
                  <div key={idx}>
                    <p className="text-base md:text-[17px] leading-[1.9] text-foreground-800 mb-5">
                      {para}
                    </p>
                    {idx === 1 && (
                      <figure className="my-7">
                        <div className="w-full h-56 md:h-72 rounded-lg overflow-hidden bg-background-200">
                          <img
                            src="https://readdy.ai/api/search-image?query=warm%20editorial%20abstract%20texture%20background%2C%20soft%20orange%20and%20cream%20gradient%20with%20grainy%20light%20streaks%2C%20minimal%20artistic%20atmosphere%2C%20subtle%20geometric%20shapes%2C%20magazine%20feature%20visual&width=1200&height=700&seq=xmt-detail-inline&orientation=landscape"
                            alt={`${item.categoryName}内容配图 - 小蜜桃`}
                            title={`${item.title} - 小蜜桃配图`}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                        <figcaption className="mt-2 text-xs text-foreground-400 text-center">
                          图 / 小蜜桃内容配图（演示图片）
                        </figcaption>
                      </figure>
                    )}
                  </div>
                ))}

                <div className="mt-8 pt-6 border-t border-background-200 flex flex-wrap items-center gap-2">
                  <span className="text-sm text-foreground-500 mr-1">标签</span>
                  {item.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                <div className="mt-8 flex flex-wrap items-center gap-3">
                  <Link
                    to={`/category/${item.categoryKey}`}
                    className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm px-5 py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-line"></i>
                    返回 {item.categoryName}频道
                  </Link>
                  <button
                    type="button"
                    onClick={() => setShareOpen(true)}
                    className="inline-flex items-center gap-2 bg-background-50 hover:bg-background-100 border border-background-200 text-foreground-700 text-sm px-5 py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-share-forward-line"></i>
                    分享这篇内容
                  </button>
                </div>
              </div>
            </div>

            <aside className="lg:col-span-1">
              <div className="lg:sticky lg:top-24">
                <h2 className="font-heading font-semibold text-lg text-foreground-950 flex items-center gap-2">
                  <i className="ri-book-open-line text-primary-500"></i>
                  相关阅读
                </h2>
                <div className="mt-4 flex flex-col gap-4">
                  {related.map((rel, idx) => (
                    <NewsCard key={rel.id} item={rel} variant="list" index={idx} />
                  ))}
                </div>

                <div className="mt-6 bg-primary-50 border border-primary-100 rounded-lg p-5">
                  <h3 className="text-sm font-semibold text-foreground-900">
                    想找更多内容？
                  </h3>
                  <p className="mt-2 text-xs text-foreground-600 leading-relaxed">
                    试试用关键词搜索，影视、音乐与生活方式内容一网打尽。
                  </p>
                  <Link
                    to="/search"
                    className="mt-4 inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm px-4 py-2 rounded-md transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-search-line"></i>
                    去搜索
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </article>

      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} title={item.title} />
    </SiteLayout>
  );
}